import { Loader2, Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { contentDestinationFormatLabels } from "@/lib/composerCore";
import type { PendingDispatch } from "@/components/composer/composerPageHelpers";

export interface PendingDispatchDialogProps {
  pendingDispatch: PendingDispatch | null;
  dispatchingPubId: string | null;
  onCancel: () => void;
  onConfirm: (publicationId: string) => void;
}

export const PendingDispatchDialog = ({
  pendingDispatch,
  dispatchingPubId,
  onCancel,
  onConfirm,
}: PendingDispatchDialogProps) => {
  return (
    <Dialog
      open={pendingDispatch !== null}
      onOpenChange={(open) => {
        if (!open && dispatchingPubId === null) onCancel();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Confirm dispatch</DialogTitle>
          <DialogDescription>
            Review where this prepared output will go before sending.
          </DialogDescription>
        </DialogHeader>
        {pendingDispatch ? (
          <div className="space-y-3 text-sm">
            <div className="rounded-md border border-border bg-card p-3 space-y-1.5">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline" className="capitalize">{pendingDispatch.connectorType}</Badge>
                <Badge variant="secondary">{contentDestinationFormatLabels[pendingDispatch.destinationFormat]}</Badge>
              </div>
              <p>
                <span className="text-muted-foreground">Publication:</span> {pendingDispatch.publicationTitle}
              </p>
              <p>
                <span className="text-muted-foreground">Destination:</span> {pendingDispatch.connectorLabel}
              </p>
              {pendingDispatch.recipientSummary ? (
                <p className="text-muted-foreground">{pendingDispatch.recipientSummary}</p>
              ) : null}
            </div>
            {pendingDispatch.connectorType === "kit" ? (
              <p className="text-xs text-muted-foreground">
                Creates a draft in Kit — you'll send from there.
              </p>
            ) : pendingDispatch.connectorType === "wordpress" ? (
              <p className="text-xs text-muted-foreground">
                Creates a draft post on WordPress.com — you'll review and publish from there.
              </p>
            ) : null}
          </div>
        ) : null}
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={dispatchingPubId !== null}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (pendingDispatch) onConfirm(pendingDispatch.publicationId);
            }}
            disabled={dispatchingPubId !== null || pendingDispatch === null}
          >
            {dispatchingPubId !== null ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            Confirm dispatch
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
